import {useCallback, useRef, useState} from 'react';
import {botService} from '../../../services/botService';
import {
    TrackDecisionEvolutionResponse,
    TrackEvolutionEvent,
    TrackEvolutionPhase,
    TrackEvolutionQuery,
    TrackEvolutionState,
    TrackForecastBatch,
} from '../trackEvolutionTypes';

function mergeEvents(
    existing: Map<string, TrackEvolutionEvent>,
    incoming: TrackEvolutionEvent[],
): Map<string, TrackEvolutionEvent> {
    const merged = new Map(existing);
    for (const ev of incoming) {
        const key = ev.eventType === 'ORIGINAL_DECISION'
            ? `ORIGINAL:${ev.decision?.decisionId ?? 'unknown'}`
            : `TRACK:${ev.run?.runId ?? 'unknown'}`;
        merged.set(key, ev);
    }
    return merged;
}

function withFocusEvent(
    events: TrackEvolutionEvent[],
    focusEvent: TrackEvolutionEvent | null,
): TrackEvolutionEvent[] {
    return focusEvent ? [...events, focusEvent] : events;
}

function mergeForecasts(
    existing: Map<number, TrackForecastBatch>,
    incoming: TrackForecastBatch[],
): Map<number, TrackForecastBatch> {
    const merged = new Map(existing);
    for (const f of incoming) {
        merged.set(f.forecastId, f);
    }
    return merged;
}

function initialState(): TrackEvolutionState {
    return {
        unit: null,
        originalDecision: null,
        originalAction: null,
        eventsByKey: new Map(),
        forecastsById: new Map(),
        chart: null,
        page: null,
        focusRunId: null,
        loading: false,
        loadingOlder: false,
        error: null,
        lastUpdatedAtMs: null,
        warningCodes: [],
    };
}

export function useTrackEvolution() {
    const [phase, setPhase] = useState<TrackEvolutionPhase>('BOOTSTRAPPING');
    const [state, setState] = useState<TrackEvolutionState>(initialState);
    const epochRef = useRef(0);
    const activeUnitIdRef = useRef<number | null>(null);

    const fetchEvolution = useCallback(async (
        query: TrackEvolutionQuery,
        signal: AbortSignal,
    ): Promise<TrackDecisionEvolutionResponse | null> => {
        const params = new URLSearchParams();
        if (query.focusRunId) params.set('focusRunId', String(query.focusRunId));
        if (query.beforeSlotTimeMs) params.set('beforeSlotTimeMs', String(query.beforeSlotTimeMs));
        if (query.beforeRunId) params.set('beforeRunId', String(query.beforeRunId));
        if (query.limit) params.set('limit', String(query.limit));
        const resp = await botService.queryTrackEvolution(query.trackUnitId, params, signal);
        if (!resp.success) {
            throw new Error(resp.message || '获取演进数据失败');
        }
        return resp.data as TrackDecisionEvolutionResponse;
    }, []);

    const bootstrap = useCallback(async (trackUnitId: number, focusRunId?: number) => {
        const epoch = ++epochRef.current;
        activeUnitIdRef.current = trackUnitId;
        setPhase('BOOTSTRAPPING');
        setState({...initialState(), loading: true, focusRunId: focusRunId ?? null});

        try {
            const controller = new AbortController();
            const data = await fetchEvolution(
                {trackUnitId, focusRunId, limit: 100},
                controller.signal,
            );
            if (epoch !== epochRef.current || !data) return;

            setState(prev => ({
                ...prev,
                unit: data.unit,
                originalDecision: data.originalDecision,
                originalAction: data.originalAction,
                eventsByKey: mergeEvents(new Map(), withFocusEvent(data.events, data.focusEvent)),
                forecastsById: mergeForecasts(new Map(), data.forecasts),
                chart: data.chart,
                page: data.page,
                focusRunId: focusRunId ?? null,
                loading: false,
                lastUpdatedAtMs: data.chart.dataUpdatedAtMs,
                warningCodes: data.warningCodes || [],
            }));

            if (!data.unit.trackingActive) {
                setPhase(data.unit.status === 'PENDING_FILL' ? 'STATIC_PENDING' : 'STATIC_ENDED');
            } else {
                setPhase('ACTIVE');
            }
        } catch (err: any) {
            if (epoch !== epochRef.current) return;
            setState(prev => ({...prev, loading: false, error: err.message || '加载失败'}));
            setPhase('ERROR');
        }
    }, [fetchEvolution]);

    const loadOlder = useCallback(async () => {
        setState(prev => ({...prev, loadingOlder: true}));
        const unitId = activeUnitIdRef.current;
        if (!unitId || !state.page?.hasMore) {
            setState(prev => ({...prev, loadingOlder: false}));
            return;
        }
        try {
            const data = await fetchEvolution(
                {
                    trackUnitId: unitId,
                    beforeSlotTimeMs: state.page.nextBeforeSlotTimeMs ?? undefined,
                    beforeRunId: state.page.nextBeforeRunId ?? undefined,
                    limit: 100,
                },
                new AbortController().signal,
            );
            if (!data || unitId !== activeUnitIdRef.current) {
                setState(prev => ({...prev, loadingOlder: false}));
                return;
            }
            setState(prev => ({
                ...prev,
                eventsByKey: mergeEvents(prev.eventsByKey, data.events),
                forecastsById: mergeForecasts(prev.forecastsById, data.forecasts),
                page: {
                    ...data.page,
                    loadedTrackCount: Math.min(
                        data.page.totalTrackCount,
                        (prev.page?.loadedTrackCount ?? 0) + data.page.loadedTrackCount,
                    ),
                },
                loadingOlder: false,
                lastUpdatedAtMs: data.chart.dataUpdatedAtMs,
                warningCodes: [...new Set([...prev.warningCodes, ...(data.warningCodes || [])])],
            }));
        } catch {
            setState(prev => ({...prev, loadingOlder: false}));
        }
    }, [state.page, fetchEvolution]);

    const poll = useCallback(async (signal: AbortSignal) => {
        const unitId = activeUnitIdRef.current;
        if (!unitId) return;
        const epoch = epochRef.current;
        try {
            const data = await fetchEvolution({trackUnitId: unitId, limit: 100}, signal);
            if (signal.aborted || !data) return;
            if (epoch !== epochRef.current) return;
            setState(prev => {
                const hasLoadedOlderPages = (prev.page?.loadedTrackCount ?? 0) > data.page.loadedTrackCount;
                return {
                    ...prev,
                    unit: data.unit,
                    originalDecision: data.originalDecision,
                    originalAction: data.originalAction,
                    eventsByKey: mergeEvents(prev.eventsByKey, withFocusEvent(data.events, data.focusEvent)),
                    forecastsById: mergeForecasts(prev.forecastsById, data.forecasts),
                    chart: data.chart,
                    page: hasLoadedOlderPages && prev.page
                        ? {
                            ...prev.page,
                            totalTrackCount: data.page.totalTrackCount,
                            loadedTrackCount: Math.min(prev.page.loadedTrackCount, data.page.totalTrackCount),
                        }
                        : data.page,
                    lastUpdatedAtMs: Math.max(data.chart.dataUpdatedAtMs, prev.lastUpdatedAtMs ?? 0),
                    warningCodes: [...new Set([...prev.warningCodes, ...(data.warningCodes || [])])],
                };
            });
            if (!data.unit.trackingActive) {
                setPhase(data.unit.status === 'PENDING_FILL' ? 'STATIC_PENDING' : 'STATIC_ENDED');
            }
        } catch {
            /* poll error, keep current data */
        }
    }, [fetchEvolution]);

    const close = useCallback(() => {
        epochRef.current++;
        activeUnitIdRef.current = null;
        setPhase('BOOTSTRAPPING');
        setState(initialState());
    }, []);

    return {
        phase,
        state,
        bootstrap,
        loadOlder,
        poll,
        close,
    };
}
